import React, { useRef, useState } from 'react';
import { ArrowLeft, BookOpen, Trophy, Compass, Swords, History, Scale, Medal } from 'lucide-react';
import { useI18n } from '../i18n';

type Lang = 'zh' | 'en' | 'ja';
type Localized = { zh: string; en: string; ja: string };

const localized = (value: Localized, lang: Lang) => value[lang] ?? value.zh;

const flag = (code: string) => {
  const map: Record<string, string> = {
    JPN: '🇯🇵', JP: '🇯🇵', USA: '🇺🇸', US: '🇺🇸', ROU: '🇷🇴', ROM: '🇷🇴',
    ITA: '🇮🇹', DEN: '🇩🇰', GBR: '🇬🇧', FR: '🇫🇷', FIN: '🇫🇮', ISR: '🇮🇱',
  };
  return map[code] ?? '🏳️';
};

const uiText: Record<Lang, {
  title: string;
  subtitle: string;
  source: string;
  navLabel: string;
}> = {
  zh: { title: '百科', subtitle: 'KENDAMA WIKI', source: '内容整理自英文维基百科 Kendama 词条', navLabel: '目录' },
  en: { title: 'Wiki', subtitle: 'KENDAMA WIKI', source: 'Adapted from the English Wikipedia article on Kendama', navLabel: 'On this page' },
  ja: { title: '百科', subtitle: 'KENDAMA WIKI', source: '英語版 Wikipedia の Kendama の項目より整理', navLabel: '目次' },
};

// 章节定义
type SectionMeta = { id: string; icon: React.ReactNode; title: Localized; subtitle: Localized };
const sections: SectionMeta[] = [
  { id: 'overview', icon: <BookOpen size={15} />, title: { zh: '概述', en: 'Overview', ja: '概要' }, subtitle: { zh: '什么是剑玉', en: 'What is Kendama', ja: 'けん玉とは' } },
  { id: 'structure', icon: <Compass size={15} />, title: { zh: '结构与术语', en: 'Structure & Terms', ja: '構造と用語' }, subtitle: { zh: '构造 · 穿线 · 握法', en: 'Anatomy · Stringing · Grips', ja: '構造 · 紐通し · 持ち方' } },
  { id: 'gameplay', icon: <Swords size={15} />, title: { zh: '玩法与招式', en: 'Gameplay & Tricks', ja: '遊び方と技' }, subtitle: { zh: '按握法分类的经典招式', en: 'Classic tricks by grip', ja: '持ち方別の基本技' } },
  { id: 'history', icon: <History size={15} />, title: { zh: '历史', en: 'History', ja: '歴史' }, subtitle: { zh: '起源与型号演变', en: 'Origins & model evolution', ja: '起源とモデルの変遷' } },
  { id: 'rules', icon: <Scale size={15} />, title: { zh: '竞赛规则', en: 'Competition Rules', ja: '競技ルール' }, subtitle: { zh: '四种竞技形式', en: 'Four competition formats', ja: '4つの競技形式' } },
  { id: 'competitions', icon: <Trophy size={15} />, title: { zh: '重大赛事', en: 'Major Competitions', ja: '主要大会' }, subtitle: { zh: '世界级赛事与冠军', en: 'World-class events & champions', ja: '世界大会とチャンピオン' } },
  { id: 'champions', icon: <Medal size={15} />, title: { zh: '历届冠军一览', en: 'Champions', ja: '歴代チャンピオン一覧' }, subtitle: { zh: '世界杯 · Catch and Flow · 总榜', en: 'KWC · Catch and Flow · Leaderboard', ja: 'KWC · Catch and Flow · 総合' } },
];

const overviewIntro: Localized[] = [
  {
    zh: '剑玉（けん玉，意为“剑与球”）是一种传统的日本技巧玩具，如今更被视为一项技巧运动或竞技项目。它由剑身（ken）、皿胴（sarado）和球（tama）三部分组成，由一根线串联在一起。剑身一端是皿，另一端收窄成剑尖（kensaki），恰好插入球上的孔（ana）中。',
    en: 'The kendama (けん玉, "sword [and] ball") is a traditional Japanese skill toy; nowadays it is considered a skill sport or competitive discipline. It consists of a handle (ken), a pair of cups (sarado), and a ball (tama) connected by a string. One end of the ken is a cup, while the other narrows into a spike (kensaki) that fits into the hole (ana) of the tama.',
    ja: 'けん玉（“剣と玉”）は日本の伝統的なスキルトイで、現在ではスキルスポーツ・競技としても認識されています。剣（けん）、皿胴、玉が一本の糸でつながっており、剣の片側は皿、もう片側は剣先（けんさき）に細くなり、玉の穴（あな）にはまります。',
  },
  {
    zh: '剑玉的起源尚有争议，但普遍认为其始于 17 或 18 世纪。传入日本后，在江户时代逐渐演变并传播到世界各地。如今世界各地都举办剑玉赛事，其中规模最大的是每年在日本举办的剑玉世界杯（KWC）。',
    en: 'The origins of kendama are disputed, but it is generally believed to date back to the 17th or 18th century. It evolved after arriving in Japan during the Edo period and has since spread worldwide. Competitions are now held around the globe, the biggest being the annual Kendama World Cup (KWC) in Japan.',
    ja: 'けん玉の起源には諸説ありますが、17〜18世紀に始まったと考えられています。江戸時代に日本へ伝わり進化し、現在では世界中に広がりました。各地で大会が開催され、最大の大会は毎年日本で行われる Kendama World Cup（KWC）です。',
  },
];

// 结构部件
type PartItem = { en: string; ja: string; kanji: string; zh: string };
const parts: PartItem[] = [
  { en: 'Ken', ja: 'Ken', kanji: '剣', zh: '剑身' },
  { en: 'Kensaki', ja: 'Kensaki', kanji: '剣先', zh: '剑尖' },
  { en: 'Ōzara (big cup)', ja: 'Ōzara', kanji: '大皿', zh: '大皿' },
  { en: 'Chūzara (base cup)', ja: 'Chūzara', kanji: '中皿', zh: '中皿' },
  { en: 'Kozara (small cup)', ja: 'Kozara', kanji: '小皿', zh: '小皿' },
  { en: 'Tama (ball)', ja: 'Tama', kanji: '玉', zh: '球' },
  { en: 'Ana (hole)', ja: 'Ana', kanji: '穴', zh: '孔' },
  { en: 'Ito (string)', ja: 'Ito', kanji: '糸', zh: '线' },
  { en: 'Saradō (cup body)', ja: 'Saradō', kanji: '皿胴', zh: '皿身' },
  { en: 'Kozara no fuchi', ja: 'Kozara no fuchi', kanji: '小皿のふち', zh: '小皿边缘' },
  { en: 'Ōzara no fuchi', ja: 'Ōzara no fuchi', kanji: '大皿のふち', zh: '大皿边缘' },
  { en: 'Suberidome', ja: 'Suberidome', kanji: 'すべり止め', zh: '防滑层' },
  { en: 'Kenjiri', ja: 'Kenjiri', kanji: 'けんじり', zh: '剑尾' },
  { en: 'Ito toritsuke ana', ja: 'Ito toritsuke ana', kanji: '糸取り付け穴', zh: '线孔' },
];

const stringingSteps: Localized[] = [
  { zh: '将线的一端穿过球上的小孔，直到线从大孔（穴）穿出。', en: 'Put one end of the string through the small hole in the tama until it comes out the big hole (ana).', ja: '糸の一端を玉の小さな穴に通し、大きな穴（あな）から出します。' },
  { zh: '在线端套上珠子（或迷你轴承）并打结固定。', en: 'Put the bead on the end of the string and tie a knot to lock it in.', ja: '糸の先にビーズ（またはベアリング）を通して結び、固定します。' },
  { zh: '将线的另一端穿过皿胴上的两个孔之一（惯用手决定穿哪个孔）。', en: 'Thread the other end through one of the two holes in the sarado (which hole depends on handedness).', ja: '糸のもう一方の端を皿胴の2つの穴のうち1つに通します（利き手によって穴が変わります）。' },
  { zh: '将线穿过剑身上的孔。', en: 'Lead the string through the hole in the ken.', ja: '糸を剣の穴に通します。' },
  { zh: '在线末端打结，防止线从剑身滑出。', en: 'Tie a knot at that end so the string does not slip through the ken.', ja: 'その端を結び、糸が剣から抜けないようにします。' },
  { zh: '将皿胴放回剑身上方。', en: 'Place the sarado down on top of the ken.', ja: '皿胴を剣の上に重ねます。' },
];

type GripItem = { name: Localized; desc: Localized };
const grips: GripItem[] = [
  {
    name: { zh: '剑握', en: 'Ken grip', ja: '剣持ち' },
    desc: { zh: '五根手指握住剑身，剑尖朝上，大皿（或小皿）朝向身体。', en: 'Hold the ken with all five fingers, spike up, with the big (or small) cup facing the body.', ja: '剣を5本の指で握り、剣先を上に、大皿（または小皿）を体側に向けます。' },
  },
  {
    name: { zh: '皿握', en: 'Sara grip', ja: '皿持ち' },
    desc: { zh: '拇指与食指置于皿胴与剑身交汇处的下方。', en: 'Hold the ken with the thumb and index finger below the intersection of the sarado and ken.', ja: '親指と人差し指を皿胴と剣の接合部の下に置きます。' },
  },
  {
    name: { zh: '皿握（稳定式）', en: 'Sara grip (stabilized)', ja: '皿持ち（安定型）' },
    desc: { zh: '在皿握基础上，将中指和无名指垫在小皿或大皿下方。', en: 'In addition to the thumb and index finger, place the middle and ring fingers beneath the small or big cup.', ja: '親指と人差し指に加え、中指と薬指を小皿または大皿の下に添えます。' },
  },
  {
    name: { zh: '球握', en: 'Tama grip', ja: '玉持ち' },
    desc: { zh: '用指尖握住球，使孔朝上。', en: 'Hold the ball (tama) with the fingertips, hole facing up.', ja: '玉を指先で持ち、穴を上に向けます。' },
  },
  {
    name: { zh: '蜡烛握', en: 'Candle grip', ja: 'キャンドル持ち' },
    desc: { zh: '剑尖朝下，用拇指、食指和中指三根手指握住剑身。', en: 'Face the ken spike-down and hold it with three fingers: index, middle, and thumb.', ja: '剣先を下に向け、親指・人差し指・中指の3本で剣を握ります。' },
  },
];

type TrickItem = { name: string; nameZh: string; desc: Localized };
type TrickGroup = { grip: Localized; tricks: TrickItem[] };

const trickGroups: TrickGroup[] = [
  {
    grip: { zh: '剑握招式', en: 'Ken grip tricks', ja: '剣持ちの技' },
    tricks: [
      { name: 'Spike', nameZh: '止剑', desc: { zh: '利用球孔与剑尖，将球垂直拉起后以剑尖接住球孔。', en: 'Pull the ball straight up and catch the hole on the spike.', ja: '玉を真上に引き上げ、穴を剣先で受けます。' } },
      { name: 'Swing Spike', nameZh: '游泳', desc: { zh: '刺剑的变体：将球摆动一周，使孔旋转 360° 后落在剑尖上。', en: 'A variation of the Spike: swing the ball so the hole rotates 360° before landing on the spike.', ja: 'スパイクの派生技。玉を振って穴を360°回転させ、剣先に乗せます。' } },
      { name: 'Around Japan', nameZh: '日本一周', desc: { zh: '依次完成小皿 → 大皿 → 剑尖（或大皿 → 小皿 → 剑尖）的组合。', en: 'A combination of the small cup, big cup, and spike (small→big→spike, or big→small→spike).', ja: '小皿→大皿→剣先（または大皿→小皿→剣先）の連続技です。' } },
      { name: 'Around the World', nameZh: '世界一周', desc: { zh: '在“Around the Block”基础上追加剑尖，完成完整环绕。', en: 'Similar to "Around the Block" with the addition of the spike.', ja: '「Around the Block」に剣先を加えた一周技です。' } },
      { name: 'Bird', nameZh: '鸟', desc: { zh: '将球孔平衡在大皿或小皿边缘，同时球身倚靠在剑尖上。', en: 'Balance the hole of the ball on the big (or small) cup edge while the ball leans against the spike.', ja: '玉の穴を大皿（または小皿）のふちに乗せ、剣先に玉を寄りかからせます。' } },
    ],
  },
  {
    grip: { zh: '皿握招式', en: 'Sara grip tricks', ja: '皿持ちの技' },
    tricks: [
      { name: 'Moshikame', nameZh: '皿上来回', desc: { zh: '大皿与中皿之间的快速往复组合。', en: 'A rapid combination of the big cup and the base cup.', ja: '大皿と中皿を素早く往復する連続技です。' } },
      { name: 'Clack back', nameZh: '棒球', desc: { zh: '让球从大皿滑落，用剑尾击打球后重新接回大皿。', en: 'Let the ball fall from the big cup, "clack" it with the back end of the ken, and catch it on the big cup.', ja: '玉を大皿から落とし、剣の後端で打って再び大皿で受けます。' } },
    ],
  },
  {
    grip: { zh: '球握招式', en: 'Tama grip tricks', ja: '玉持ちの技' },
    tricks: [
      { name: 'Airplane', nameZh: '飞机', desc: { zh: '让剑身在半空旋转 180°，以球孔接住剑尖。', en: 'Pull the ken so it rotates 180° and catch the spike in the hole.', ja: '剣を空中で180°回転させ、穴で剣先を受けます。' } },
      { name: 'Lighthouse', nameZh: '灯台', desc: { zh: '将剑身直立平衡在球上。', en: 'Balance the ken upright on top of the ball.', ja: '剣を玉の上に直立させてバランスを取ります。' } },
      { name: 'Lunar', nameZh: '月面', desc: { zh: '将大皿朝下平衡在球上。', en: 'Balance the ken on its big cup on top of the ball.', ja: '大皿を下にして玉の上に剣を乗せます。' } },
    ],
  },
  {
    grip: { zh: '蜡烛握招式', en: 'Candle grip tricks', ja: 'キャンドル持ちの技' },
    tricks: [
      { name: 'Candlestick', nameZh: '烛台', desc: { zh: '将球拉起或摆起，落在中皿上。', en: 'Pull or swing the ball up onto the base cup.', ja: '玉を引き上げるか振り上げて中皿に乗せます。' } },
    ],
  },
];

const historyOrigin: Localized[] = [
  {
    zh: '剑玉起源尚无定论。一种观点认为它是欧洲“杯与球”游戏（bilboquet，16 世纪起源、17 世纪流行于欧洲）的变体。同一时期，剑玉被认为经丝绸之路由中国传入日本，时间约为江户时代（1600–1868）。',
    en: 'The origins are disputed. It is believed by some to be a variant of the French cup-and-ball game bilboquet (dating to the 16th century). In the same period, kendama is believed to have reached Japan from China via the Silk Road during the Edo period (1600–1868).',
    ja: '起源には諸説あります。16世紀に遡るフランスの「ビルボケ」の派生とする説があります。同時期、けん玉は江戸時代（1600–1868）にシルクロード経由で中国から日本へ伝わったと考えられています。',
  },
  {
    zh: '广岛县廿日市市（Hatsukaichi）因成为首个剑玉制造地，被视为现代日本剑玉的发源地。1919 年，江草滨松（Hamagatsu Ekusa）在吴市创造出如今广为人知的剑玉造型。20 世纪初，该玩具拥有两个侧皿，被称为“日月球”，因球象征太阳、皿形似新月而得名。',
    en: 'Hatsukaichi in Hiroshima Prefecture is considered the birthplace of the modern Japanese kendama as the first manufacturing hub. Hamagatsu Ekusa created the widely recognized shape in 1919 in Kure. In the early 20th century the toy had two side cups and was called the "jitsugetsu ball" (sun and moon ball).',
    ja: '広島県廿日市市は最初の製造地として現代けん玉の発祥の地とされています。1919年に江草浜松が呉市で現在の形状を作りました。20世紀初頭の玩具は側面に2つの皿を持ち、玉が太陽、皿が三日月に似ていることから「日月ボール」と呼ばれました。',
  },
];

type ModelItem = { name: Localized; desc: Localized };
const historyModels: ModelItem[] = [
  { name: { zh: '鹿角与球', en: 'Deer horn and ball', ja: '鹿角と玉' }, desc: { zh: '剑玉最初传入日本时的形态，即鹿角系于球上。', en: 'The earliest form on arrival in Japan: a deer horn attached to a ball.', ja: '日本伝来当初の形で、鹿の角を玉に取り付けたもの。' } },
  { name: { zh: '竹与球', en: 'Bamboo and ball', ja: '竹と玉' }, desc: { zh: '因鹿角成本过高，以竹替代鹿角的形态。', en: 'A bamboo piece replaced the deer horn to save resources.', ja: '鹿角のコストが高いため竹で代用した形。' } },
  { name: { zh: '剑与球', en: 'Ken and ball', ja: '剣と玉' }, desc: { zh: '开始接近现代造型：剑身与球以线相连。', en: 'Resembles today\'s shape: a ken strung to the ball.', ja: '剣を玉に糸でつないだ、現代に近い形。' } },
  { name: { zh: '日月球', en: 'Jitsugetsu ball', ja: '日月ボール' }, desc: { zh: '江草于 1919 年制作的首款型号，1921 年曾年产高达 30 万只。', en: 'Ekusa\'s first model (1919), produced up to 300,000 times a year by 1921.', ja: '1919年の江草の初モデル。1921年には年30万個が生産されました。' } },
  { name: { zh: 'S 型（Shinma）', en: 'S (Shinma) Type', ja: 'S型（Shinma）' }, desc: { zh: '1975 年由东京剑玉俱乐部会长新间英雄发明的首款竞技型剑玉。', en: 'The first competition-style kendama, invented in 1975 by Hideo Shinma of the Tokyo Kendama Club.', ja: '1975年、東京けん玉クラブの新間英雄が考案した初の競技用モデル。' } },
  { name: { zh: 'F 型（Fujiwara / F16）', en: 'F (Fujiwara) Type (F16)', ja: 'F型（Fujiwara / F16）' }, desc: { zh: '1978 年由藤原一诚发明，皿胴两侧各有两个小孔，线材更坚韧，支持左右手切换。', en: 'Invented by Issei Fujiwara in 1978, with two small holes in each side of the sarado and a sturdier string, allowing left/right switching.', ja: '1978年、藤原一誠が考案。皿胴の両側に2つの穴と丈夫な糸を持ち、左右切り替えが可能。' } },
  { name: { zh: 'Tortoise 系列', en: 'Tortoise', ja: 'Tortoise' }, desc: { zh: 'S 型停产后（1990）的改良款，型号 T-8 / T-14 / T-16 / T-17，数字对应高度（厘米），2012 年停产。', en: 'A take on the S-type after it was discontinued in 1990. T-8/T-14/T-16/T-17, where the number is the height in cm. Discontinued in 2012.', ja: 'S型終了（1990）後の改良モデル。T-8/T-14/T-16/T-17の数字は高さ（cm）。2012年生産終了。' } },
  { name: { zh: 'F16-2', en: 'F16-2', ja: 'F16-2' }, desc: { zh: 'F16 的第二版，2001 年发布，线孔位置微调使剑玉能更流畅地旋转。', en: 'The second version of the F16 (2001); the string hole was moved slightly for a new fluid rotation.', ja: 'F16の第二版（2001年）。糸穴の位置をずらし、新しい回転を可能にしました。' } },
];

type RuleItem = { name: Localized; short: Localized; points: Localized[]; note?: Localized };
const rules: RuleItem[] = [
  {
    name: { zh: '速度梯（Speed Ladder）', en: 'Speed Ladder', ja: 'スピードラダー' },
    short: { zh: '比拼谁最快完成一整套规定招式。选手按给定顺序依次完成招式，用时最短者获胜；按水平分为初级、中级、高级/职业组。', en: 'A race to finish a set of tricks the fastest. Players race through a given trick order; the fastest finisher wins. Divisions: beginner, intermediate, and advanced/pro.', ja: '一連の技をどれだけ速く完成できるかを競います。与えられた順に技をこなし、最速の選手が勝利。初級・中級・上級/プロの部門があります。' },
    points: [],
  },
  {
    name: { zh: '公开组（Open Division）', en: 'Open Division', ja: 'オープンディビジョン' },
    short: { zh: '1 对 1 的对抗赛制。每回合双方轮流随机抽取一个招式，每个招式最多进行三次交换（往来攻防）。先得 3 分者获胜（决赛先得 5 分）。', en: 'Head-to-head 1v1. Players take turns drawing a trick; each trick allows up to three exchanges. First to 3 points wins (first to 5 in the final).', ja: '1対1の対戦形式。交代で技をランダムに引き、各技は最大3回の攻防。先に3ポイント（決勝は5ポイント）で勝利。' },
    points: [
      { zh: '若一方完成、另一方失误，成功方得 1 分，另一方再抽新招式。', en: 'If one completes and the other misses, the successful player earns 1 point and the other draws again.', ja: '一方が成功し他方が失敗した場合、成功者に1ポイント、他方が新たに引きます。' },
      { zh: '若双方都完成或都失误，则第一次交换结束，先手获得第二次机会重新尝试。', en: 'If both complete or miss, the exchange ends and the first player gets another attempt.', ja: '両者成功または失敗の場合、その攻防は終了し、先手がもう一度挑戦します。' },
      { zh: '若三回合双方都完成或都失误，则该招式作废，由后手抽取新招式。', en: 'If both complete or miss in all three bouts, the trick is discarded and the second player draws a new one.', ja: '3回とも両者成功または失敗なら、その技は破棄され、後手が新たに引きます。' },
    ],
    note: { zh: '赛事冠军通常指公开组冠军。', en: 'The labeled champion of each event usually refers to the Open Division winner.', ja: '各大会のチャンピオンは通常オープンディビジョンの勝者を指します。' },
  },
  {
    name: { zh: '自由式（Freestyle）', en: 'Freestyle', ja: 'フリースタイル' },
    short: { zh: '1 对 1 的表演赛制，由 3 或 5 名裁判打分。每轮双方各进行两次 45 秒的表演，可自由选择招式。', en: 'Head-to-head 1v1 judged by a panel of 3–5 judges. Players alternate two 45-second routines each, performing any tricks they choose.', ja: '3〜5名の審査員が判定する1対1の形式。各選手が45秒の演技を2回ずつ行い、好きな技を披露します。' },
    points: [
      { zh: '裁判从创意（Creativity）、稳定性（Consistency）、难度（Difficulty）三个维度评判。', en: 'Judges score on three categories: Creativity, Consistency, and Difficulty.', ja: '審査は創造性・安定性・難易度の3項目で行われます。' },
      { zh: '得票多的一方赢得该回合。', en: 'The player with the most votes wins the round.', ja: 'より多くの票を得た選手がラウンドを制します。' },
    ],
  },
  {
    name: { zh: '剑玉世界杯（KWC）', en: 'KWC', ja: 'KWC' },
    short: { zh: '赛前线上公布 120 个招式，按难度分为 1–12 级（每级 10 个）。比赛分两天：第一天资格赛，第二天决赛。', en: '120 tricks are released online, split into levels 1–12 (10 tricks each). Day 1 is Qualifying; Day 2 is Finals.', ja: '120の技が事前に公開され、難易度1〜12のレベル（各10技）に分けられます。1日目は予選、2日目は決勝です。' },
    points: [
      { zh: '第一天：选手从 1–10 级中选 12 个招式，分两轮（每轮 6 个），每轮限时 3 分钟完成尽可能多的招式，前 25 名晋级。', en: 'Day 1: choose 12 tricks from levels 1–10, split into two rounds of six; 3 minutes per round. Top 25 advance.', ja: '1日目：レベル1〜10から12技を選び、2ラウンド（各6技）に分け、各ラウンド3分。上位25名が進出。' },
      { zh: '第一天计分：每个招式的分值等于其等级面值（如 6 级招式 = 6 分）。', en: 'Day 1 scoring: each trick is worth its level value (e.g. level 6 = 6 points).', ja: '1日目の得点はレベルの数字（例：レベル6=6点）。' },
      { zh: '第二天：从资格赛最低分到最高分依次出场，每人 3 分钟，从 3–12 级中不限次数完成招式（每个招式只能做一次），得分最高者夺冠。', en: 'Day 2: players compete from lowest to highest qualifier; 3 minutes to do unlimited tricks from levels 3–12 (each once). Highest score wins.', ja: '2日目：予選下位から順に出場し、3分間でレベル3〜12の技を無制限に（各1回まで）行い、最高得点者が優勝。' },
      { zh: '第二天计分：招式分值为其等级的平方（如 6 级 = 36 分），11 级为 151 分、12 级为 194 分。', en: 'Day 2 scoring: a trick is worth its level squared (level 6 = 36), except level 11 (151) and level 12 (194).', ja: '2日目の得点はレベルの2乗（レベル6=36点）。ただしレベル11は151点、レベル12は194点。' },
    ],
    note: { zh: '2025 年及以前的旧规则，2026 年起开始实行新规。', en: 'Old rules for 2025 and earlier; new rules take effect from 2026.', ja: '2025年以前の旧ルール。2026年から新ルールが適用されます。' },
  },
];

type CompetitionItem = { name: Localized; region: Localized; desc: Localized };
const competitions: CompetitionItem[] = [
  {
    name: { zh: '剑玉世界杯', en: 'Kendama World Cup (KWC)', ja: 'Kendama World Cup（KWC）' },
    region: { zh: '日本 · 广岛县廿日市市', en: 'Hatsukaichi, Hiroshima, Japan', ja: '日本 · 広島県廿日市市' },
    desc: { zh: '自 2014 年起每年夏季举办，为期两天，是全球规模最大的剑玉赛事。2018 年即有 18 个国家的 415 名选手参赛、4.9 万人观赛。现场还有众多展商、游戏与表演，入场免费。', en: 'An annual two-day summer event since 2014 in Hatsukaichi, Japan, and the largest kendama competition in the world. In 2018, 415 competitors from 18 countries competed before 49,000 spectators. Admission is free.', ja: '2014年から毎年夏に開催される2日間の大会で、世界最大規模です。2018年には18カ国415名が出場し、4.9万人が観戦。出展やパフォーマンスもあり、入場無料です。' },
  },
  {
    name: { zh: 'Catch and Flow', en: 'Catch and Flow', ja: 'Catch and Flow' },
    region: { zh: '日本 · 东京', en: 'Tokyo, Japan', ja: '日本 · 東京' },
    desc: { zh: '自由式世界锦标赛，2014 年 9 月首次在东京市中心举办，开创了自由式剑玉的新表演与评判方式。约 60 名顶尖选手各自进行 90 秒表演，16 人晋级后进行 2×45 秒的 1 对 1 对决。', en: 'The Freestyle World Championship, first held in downtown Tokyo in September 2014. Around 60 top players perform 90 seconds each; 16 finalists then battle 1v1 with 2×45 seconds.', ja: '2014年9月に東京で初開催されたフリースタイル世界選手権。約60名が90秒ずつ演技し、16名が2×45秒の1対1で決勝を争います。' },
  },
  {
    name: { zh: '北美剑玉公开赛', en: 'North American Kendama Open (NAKO)', ja: 'North American Kendama Open（NAKO）' },
    region: { zh: '美国 · 明尼苏达州', en: 'Minnesota, USA', ja: 'アメリカ · ミネソタ州' },
    desc: { zh: '前身为“明尼苏达剑玉公开赛”，自 2013 年起每年秋季在明尼苏达举办，时长从一天逐步扩展到三天。设有初级/中级速度梯、业余/职业公开组与自由式。', en: 'Formerly the Minnesota Kendama Open, held every fall in Minnesota since 2013. It grew from one day to three, offering beginner/intermediate speed ladders, amateur/pro open, and freestyle.', ja: '旧「Minnesota Kendama Open」。2013年から毎年秋にミネソタで開催され、1日から3日制に拡大。初級/中級ラダー、アマ/プロオープン、フリースタイルがあります。' },
  },
  {
    name: { zh: '欧洲剑玉锦标赛', en: 'European Kendama Championship (EKC)', ja: 'European Kendama Championship（EKC）' },
    region: { zh: '荷兰 · 莱顿', en: 'Leiden, Netherlands', ja: 'オランダ · ライデン' },
    desc: { zh: '欧洲最大的剑玉赛事，2025 年参赛人数超 700 人，线上观众更多。主项目为自由式，2022 年起增设公开组。以“最像嘻哈演唱会的剑玉赛事”著称，目前由 Grain Theory 职业选手 Teodore Fiorina 主办。', en: 'The biggest kendama event in Europe with 700+ attendees in 2025. Its main event is Freestyle (Open added in 2022). Known as the world\'s most exciting event, hosted by Grain Theory pro Teodore Fiorina.', ja: 'ヨーロッパ最大の大会で、2025年は700名以上が参加。メインはフリースタイルで、2022年からオープンも開催。Grain Theory の Teodore Fiorina が主催しています。' },
  },
  {
    name: { zh: '边境之战', en: 'Battle at the Border (BATB)', ja: 'Battle at the Border（BATB）' },
    region: { zh: '美国 · 田纳西州', en: 'Tennessee, USA', ja: 'アメリカ · テネシー州' },
    desc: { zh: '美国历史最悠久的年度剑玉赛事，由肯塔基品牌 Sol Kendamas 主办，每年一月的第一个周末在田纳西举行。2011 年 6 月在纳什维尔举办了首场公开剑玉对战。', en: 'The longest-standing annual kendama competition in the US, hosted by Kentucky-based Sol Kendamas on the first weekend of January in Tennessee. The first public battle was held in Nashville in June 2011.', ja: 'アメリカ最古の年次大会。ケンタッキーの Sol Kendamas が主催し、毎年1月第1週末にテネシーで開催。2011年6月にナッシュビルで最初の公開バトルが行われました。' },
  },
];

type PodiumRow = { year: string; winner: [string, string]; second: [string, string]; third: [string, string] };
const kwcWinners: PodiumRow[] = [
  { year: '2025', winner: ['JPN', 'Ryoga Kawamoto'], second: ['JPN', 'Takuya Igarashi'], third: ['ROU', 'Adrian Vilau'] },
  { year: '2024', winner: ['JPN', 'Ryoga Kawamoto'], second: ['JPN', 'Takuya Igarashi'], third: ['JPN', 'Hiroto Motohashi'] },
  { year: '2023', winner: ['JPN', 'Takuya Igarashi'], second: ['JPN', 'Hiroto Motohashi'], third: ['JPN', 'Maharu Tashiro'] },
  { year: '2022', winner: ['JPN', 'Takuya Igarashi'], second: ['JPN', 'So Kanada'], third: ['JPN', 'Hiroto Motohashi'] },
  { year: '2021', winner: ['JPN', 'Yasuhito Chiba'], second: ['JPN', 'Maharu Tashiro'], third: ['ROU', 'Tiberiu Terteleac'] },
  { year: '2020', winner: ['JPN', 'Takuya Igarashi'], second: ['JPN', 'Hiroto Motohashi'], third: ['JPN', 'Maharu Tashiro'] },
  { year: '2019', winner: ['JPN', 'Rui Sora'], second: ['USA', 'Nick Gallagher'], third: ['JPN', 'Hiroto Motohashi'] },
  { year: '2018', winner: ['USA', 'Nick Gallagher'], second: ['JPN', 'So Kanada'], third: ['JPN', 'Rumina Yakabi'] },
  { year: '2017', winner: ['JPN', 'So Kanada'], second: ['USA', 'Bryson Lee'], third: ['ISR', 'Liad Kotlarker'] },
  { year: '2016', winner: ['USA', 'Bryson Lee'], second: ['USA', 'Lukas Funk'], third: ['JPN', 'Kazuki Nagata'] },
  { year: '2015', winner: ['USA', 'Wyatt Bray'], second: ['USA', 'Lukas Funk'], third: ['USA', 'Nic Stodd'] },
  { year: '2014', winner: ['USA', 'Bonz Atron'], second: ['USA', 'Kris Bosh'], third: ['JPN', 'Satoru Akimoto'] },
];

const catchAndFlowWinners: PodiumRow[] = [
  { year: '2025', winner: ['JPN', 'Yasuhito Chiba'], second: ['ROU', 'Adrian Vilau'], third: ['JPN', 'Ryoga Kawamoto'] },
  { year: '2024', winner: ['—', 'N/A'], second: ['—', 'N/A'], third: ['—', 'N/A'] },
  { year: '2023', winner: ['JPN', 'Yasuhito Chiba'], second: ['JPN', 'Shinnosuke Togo'], third: ['ITA', 'Davide Leonardi'] },
  { year: '2022', winner: ['JPN', 'Shinnosuke Togo'], second: ['JPN', 'Yasuhito Chiba'], third: ['JPN', 'Hiroto Motohashi'] },
  { year: '2021', winner: ['JPN', 'Yasuhito Chiba'], second: ['JPN', 'Nobuyoshi Norioka'], third: ['JPN', 'Soma Fujita'] },
  { year: '2020', winner: ['JPN', 'Takuya Igarashi'], second: ['JPN', 'Soma Fujita'], third: ['JPN', 'Yasuhito Chiba'] },
  { year: '2019', winner: ['JPN', 'Kaito Nakajima'], second: ['JPN', 'Yasuhito Chiba'], third: ['JPN', 'Kengo Kawamura'] },
  { year: '2018', winner: ['USA', 'Bonz Atron'], second: ['USA', 'Nick Gallagher'], third: ['JPN', 'Misumi Nakao'] },
  { year: '2017', winner: ['USA', 'Bonz Atron'], second: ['JPN', 'So Kanada'], third: ['USA', 'Nick Gallagher'] },
  { year: '2016', winner: ['USA', 'Jake Fischer'], second: ['USA', 'Wyatt Bray'], third: ['USA', 'Bonz Atron'] },
  { year: '2015', winner: ['USA', 'Bonz Atron'], second: ['USA', 'Nic Stodd'], third: ['JPN', 'Taku Hatanaka'] },
  { year: '2014', winner: ['DEN', 'Thorkild May'], second: ['JPN', 'Taiga Ogawa'], third: ['USA', 'Kris Bosch'] },
];

type ChampionRow = { name: string; code: string; kwc: number; open: number; freestyle: number; total: number };
const championLeaderboard: ChampionRow[] = [
  { name: 'Nick Gallagher', code: 'USA', kwc: 2, open: 3, freestyle: 4, total: 9 },
  { name: 'Bonz Atron', code: 'USA', kwc: 1, open: 2, freestyle: 5, total: 8 },
  { name: 'Yasuhito Chiba', code: 'JPN', kwc: 1, open: 1, freestyle: 6, total: 8 },
  { name: 'Ryoga Kawamoto', code: 'JPN', kwc: 2, open: 4, freestyle: 2, total: 8 },
  { name: 'Takuya Igarashi', code: 'JPN', kwc: 3, open: 0, freestyle: 2, total: 5 },
  { name: 'Liam Rauter', code: 'USA', kwc: 0, open: 4, freestyle: 1, total: 5 },
  { name: 'Jake Fischer', code: 'USA', kwc: 0, open: 1, freestyle: 4, total: 5 },
  { name: 'Alex Mitchell', code: 'USA', kwc: 0, open: 2, freestyle: 3, total: 5 },
  { name: 'So Kanada', code: 'JPN', kwc: 1, open: 3, freestyle: 0, total: 4 },
  { name: 'Wyatt Bray', code: 'USA', kwc: 1, open: 0, freestyle: 2, total: 3 },
  { name: 'Rob Henman', code: 'GBR', kwc: 0, open: 2, freestyle: 1, total: 3 },
  { name: 'Nonoka Kyodo', code: 'JPN', kwc: 0, open: 1, freestyle: 2, total: 3 },
  { name: 'Bryson Lee', code: 'USA', kwc: 1, open: 0, freestyle: 1, total: 2 },
  { name: 'William Penniman', code: 'USA', kwc: 0, open: 2, freestyle: 0, total: 2 },
  { name: 'Christian Fraser', code: 'USA', kwc: 0, open: 1, freestyle: 1, total: 2 },
  { name: 'Lukas Funk', code: 'USA', kwc: 0, open: 1, freestyle: 1, total: 2 },
  { name: 'Zack Gallagher', code: 'USA', kwc: 0, open: 2, freestyle: 0, total: 2 },
  { name: 'Tomoya Mukai', code: 'JPN', kwc: 0, open: 2, freestyle: 0, total: 2 },
  { name: 'Kaito Nakajima', code: 'JPN', kwc: 0, open: 0, freestyle: 2, total: 2 },
  { name: 'Rolf Sandvigganer', code: 'DEN', kwc: 0, open: 1, freestyle: 1, total: 2 },
  { name: 'Rui Sora', code: 'JPN', kwc: 1, open: 0, freestyle: 0, total: 1 },
  { name: 'Kevin DeSoto', code: 'USA', kwc: 0, open: 1, freestyle: 0, total: 1 },
  { name: 'Katsuaki Schimadera', code: 'JPN', kwc: 0, open: 1, freestyle: 0, total: 1 },
  { name: 'Logan Tosta', code: 'USA', kwc: 0, open: 0, freestyle: 1, total: 1 },
  { name: 'Teodore Fiorina', code: 'FR', kwc: 0, open: 0, freestyle: 1, total: 1 },
  { name: 'Kelvin Wong', code: 'USA', kwc: 0, open: 1, freestyle: 0, total: 1 },
  { name: 'Hiroto Motohashi', code: 'JPN', kwc: 0, open: 1, freestyle: 0, total: 1 },
  { name: 'Edwin Tickner', code: 'USA', kwc: 0, open: 1, freestyle: 0, total: 1 },
  { name: 'Max Norcross', code: 'USA', kwc: 0, open: 1, freestyle: 0, total: 1 },
  { name: 'Nobuyoshi Norioka', code: 'JPN', kwc: 0, open: 1, freestyle: 0, total: 1 },
  { name: 'Jacob Schultz', code: 'USA', kwc: 0, open: 1, freestyle: 0, total: 1 },
  { name: 'Adrian Vilau', code: 'ROU', kwc: 0, open: 1, freestyle: 0, total: 1 },
  { name: 'Matthew Ballard', code: 'USA', kwc: 0, open: 0, freestyle: 1, total: 1 },
  { name: 'Hiroki Ijima', code: 'JPN', kwc: 0, open: 0, freestyle: 1, total: 1 },
  { name: 'Albert Kirvesmaki', code: 'FIN', kwc: 0, open: 0, freestyle: 1, total: 1 },
  { name: 'Thorkild May', code: 'DEN', kwc: 0, open: 0, freestyle: 1, total: 1 },
  { name: 'Misumi Nakao', code: 'JPN', kwc: 0, open: 0, freestyle: 1, total: 1 },
  { name: 'Shinnosuke Togo', code: 'JPN', kwc: 0, open: 0, freestyle: 1, total: 1 },
  { name: 'Nowa Yamada', code: 'JPN', kwc: 0, open: 0, freestyle: 1, total: 1 },
];

const podiumLabels: Record<Lang, { winner: string; second: string; third: string }> = {
  zh: { winner: '冠军', second: '亚军', third: '季军' },
  en: { winner: 'Winner', second: '2nd', third: '3rd' },
  ja: { winner: '優勝', second: '2位', third: '3位' },
};

const leaderboardLabels: Record<Lang, { player: string; kwc: string; open: string; freestyle: string; total: string }> = {
  zh: { player: '选手', kwc: 'KWC 冠军', open: '公开组冠军', freestyle: '自由式冠军', total: '总冠军' },
  en: { player: 'Player', kwc: 'KWC', open: 'Open', freestyle: 'Freestyle', total: 'Total' },
  ja: { player: '選手', kwc: 'KWC', open: 'オープン', freestyle: 'フリースタイル', total: '合計' },
};

interface KendamaWikiProps {
  onBack: () => void;
}

export const KendamaWiki: React.FC<KendamaWikiProps> = ({ onBack }) => {
  const { language } = useI18n();
  const lang = language as Lang;
  const text = uiText[lang];
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [champTab, setChampTab] = useState<string>('kwc');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    let current = sections[0].id;
    for (const section of sections) {
      const el = sectionRefs.current[section.id];
      if (el && el.getBoundingClientRect().top <= 140) {
        current = section.id;
      }
    }
    setActiveSection(current);
  };

  return (
    <div className="relative isolate flex h-full min-h-0 w-full flex-col overflow-hidden text-white">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.12),_transparent_28%),linear-gradient(180deg,_rgba(12,14,20,0.98),_rgba(5,6,10,1))]"
      />

      {/* Header */}
      <div className="relative mx-auto flex min-h-[44px] w-full max-w-5xl shrink-0 items-center justify-center px-12 pt-1 text-center">
        <button onClick={onBack} className="absolute left-3 p-2 text-white/50 transition-colors hover:text-white md:left-6" aria-label={localized({ zh: '返回', en: 'Back', ja: '戻る' }, lang)}>
          <ArrowLeft size={24} />
        </button>
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2">
            <BookOpen size={20} className="text-cyan-300" />
            <h1 className="font-pingfang text-xl font-bold tracking-[0.14em] text-cyan-300 drop-shadow-[0_0_14px_rgba(34,211,238,0.38)] md:text-3xl">
              {text.title}
            </h1>
          </div>
          <div className="mt-1 font-orbitron text-[10px] uppercase tracking-[0.28em] text-cyan-100/50 md:text-xs">
            {text.subtitle}
          </div>
        </div>
      </div>

      {/* Section nav */}
      <div className="mx-auto w-full max-w-5xl shrink-0 px-3 pt-2 md:px-6">
        <div className="flex flex-wrap justify-center gap-1.5">
          {sections.map((section) => {
            const active = activeSection === section.id;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center justify-center gap-1.5 rounded-full border px-2 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? 'border-cyan-300/60 bg-cyan-500/20 text-cyan-100 shadow-[0_0_14px_rgba(34,211,238,0.24)]'
                    : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20 hover:bg-white/10'
                }`}
              >
                <span className={active ? 'text-cyan-300' : 'text-zinc-500'}>{section.icon}</span>
                <span className="whitespace-nowrap font-pingfang">{localized(section.title, lang)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Scrollable content */}
      <div ref={scrollRef} onScroll={onScroll} className="mx-auto flex h-full w-full max-w-5xl flex-col gap-4 overflow-y-auto overflow-x-hidden px-3 py-2 pb-24 md:px-6 md:py-4 custom-scrollbar">
        {/* Overview */}
        <section ref={(el) => { sectionRefs.current.overview = el; }} className="scroll-mt-24 rounded-3xl border border-white/10 bg-black/35 p-4 shadow-[0_0_35px_rgba(34,211,238,0.06)] backdrop-blur-sm md:p-6">
          <SectionHeading icon={<BookOpen size={16} />} title={localized({ zh: '概述', en: 'Overview', ja: '概要' }, lang)} subtitle={localized({ zh: '什么是剑玉', en: 'What is Kendama', ja: 'けん玉とは' }, lang)} />
          <div className="mt-4 space-y-3">
            {overviewIntro.map((p, i) => (
              <p key={i} className="text-sm leading-relaxed text-zinc-300">{localized(p, lang)}</p>
            ))}
          </div>
        </section>

        {/* Structure */}
        <section ref={(el) => { sectionRefs.current.structure = el; }} className="scroll-mt-24 rounded-3xl border border-white/10 bg-black/35 p-4 shadow-[0_0_35px_rgba(34,211,238,0.06)] backdrop-blur-sm md:p-6">
          <SectionHeading icon={<Compass size={16} />} title={localized({ zh: '结构与术语', en: 'Structure & Terms', ja: '構造と用語' }, lang)} subtitle={localized({ zh: '构造 · 穿线 · 握法', en: 'Anatomy · Stringing · Grips', ja: '構造 · 紐通し · 持ち方' }, lang)} />

          <h3 className="mt-5 font-pingfang text-sm font-semibold text-white">{localized({ zh: '剑玉部件', en: 'Parts of the kendama', ja: 'けん玉の部品' }, lang)}</h3>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {parts.map((part) => (
              <div key={part.en} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <div className="font-orbitron text-xs font-bold text-cyan-200">{part.en}</div>
                <div className="mt-1 text-sm text-white">{part.kanji}</div>
                <div className="text-[11px] text-zinc-500">{part.zh}</div>
              </div>
            ))}
          </div>

          <h3 className="mt-6 font-pingfang text-sm font-semibold text-white">{localized({ zh: '穿线步骤', en: 'Stringing a kendama', ja: '紐の通し方' }, lang)}</h3>
          <ol className="mt-3 space-y-2">
            {stringingSteps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500/20 text-[11px] font-bold text-cyan-200">{i + 1}</span>
                <span>{localized(step, lang)}</span>
              </li>
            ))}
          </ol>

          <h3 className="mt-6 font-pingfang text-sm font-semibold text-white">{localized({ zh: '握法', en: 'Grips', ja: '持ち方' }, lang)}</h3>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {grips.map((grip) => (
              <div key={localized(grip.name, 'en')} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <div className="font-pingfang text-sm font-semibold text-white">{localized(grip.name, lang)}</div>
                <p className="mt-1 text-xs leading-relaxed text-zinc-400">{localized(grip.desc, lang)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Gameplay */}
        <section ref={(el) => { sectionRefs.current.gameplay = el; }} className="scroll-mt-24 rounded-3xl border border-white/10 bg-black/35 p-4 shadow-[0_0_35px_rgba(34,211,238,0.06)] backdrop-blur-sm md:p-6">
          <SectionHeading icon={<Swords size={16} />} title={localized({ zh: '玩法与招式', en: 'Gameplay & Tricks', ja: '遊び方と技' }, lang)} subtitle={localized({ zh: '按握法分类的经典招式', en: 'Classic tricks by grip', ja: '持ち方別の基本技' }, lang)} />
          <div className="mt-4 space-y-4">
            {trickGroups.map((group) => (
              <div key={localized(group.grip, 'en')}>
                <h3 className="font-pingfang text-sm font-semibold text-cyan-200">{localized(group.grip, lang)}</h3>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {group.tricks.map((trick) => (
                    <div key={trick.name} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                      <div className={`text-sm font-bold text-white ${lang === 'zh' ? 'font-pingfang' : 'font-orbitron'}`}>
                        {lang === 'zh' ? trick.nameZh : trick.name}
                      </div>
                      {lang === 'zh' && (
                        <div className="mt-0.5 font-orbitron text-[11px] font-semibold uppercase tracking-wide text-cyan-200/70">{trick.name}</div>
                      )}
                      <p className="mt-1 text-xs leading-relaxed text-zinc-400">{localized(trick.desc, lang)}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* History */}
        <section ref={(el) => { sectionRefs.current.history = el; }} className="scroll-mt-24 rounded-3xl border border-white/10 bg-black/35 p-4 shadow-[0_0_35px_rgba(34,211,238,0.06)] backdrop-blur-sm md:p-6">
          <SectionHeading icon={<History size={16} />} title={localized({ zh: '历史', en: 'History', ja: '歴史' }, lang)} subtitle={localized({ zh: '起源与型号演变', en: 'Origins & model evolution', ja: '起源とモデルの変遷' }, lang)} />
          <div className="mt-4 space-y-3">
            {historyOrigin.map((p, i) => (
              <p key={i} className="text-sm leading-relaxed text-zinc-300">{localized(p, lang)}</p>
            ))}
          </div>
          <h3 className="mt-6 font-pingfang text-sm font-semibold text-white">{localized({ zh: '型号演变', en: 'Models', ja: 'モデルの変遷' }, lang)}</h3>
          <div className="mt-3 space-y-2">
            {historyModels.map((model) => (
              <div key={localized(model.name, 'en')} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <div className="font-pingfang text-sm font-semibold text-white">{localized(model.name, lang)}</div>
                <p className="mt-1 text-xs leading-relaxed text-zinc-400">{localized(model.desc, lang)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Rules */}
        <section ref={(el) => { sectionRefs.current.rules = el; }} className="scroll-mt-24 rounded-3xl border border-white/10 bg-black/35 p-4 shadow-[0_0_35px_rgba(34,211,238,0.06)] backdrop-blur-sm md:p-6">
          <SectionHeading icon={<Scale size={16} />} title={localized({ zh: '竞赛规则', en: 'Competition Rules', ja: '競技ルール' }, lang)} subtitle={localized({ zh: '四种竞技形式', en: 'Four competition formats', ja: '4つの競技形式' }, lang)} />
          <p className="mt-4 text-sm leading-relaxed text-zinc-400">
            {localized({ zh: '剑玉本身没有特定的玩法规则，但所有竞技形式都有明确规则。四种竞技形式为：速度梯、公开组、自由式与剑玉世界杯（KWC）。', en: 'There are no specific rules on how to play kendama, but all competition formats are regulated. The four formats are Speed Ladder, Open Division, Freestyle, and KWC.', ja: 'けん玉の遊び方自体に特定のルールはありませんが、競技形式には明確なルールがあります。4つの形式はスピードラダー、オープン、フリースタイル、KWCです。' }, lang)}
          </p>
          <div className="mt-4 space-y-3">
            {rules.map((rule) => (
              <div key={localized(rule.name, 'en')} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="font-pingfang text-sm font-semibold text-cyan-200">{localized(rule.name, lang)}</div>
                {rule.note && (
                  <p className="mt-1.5 inline-flex rounded-md border border-amber-300/40 bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-200">
                    {localized(rule.note, lang)}
                  </p>
                )}
                <p className="mt-1.5 text-sm leading-relaxed text-zinc-300">{localized(rule.short, lang)}</p>
                {rule.points.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {rule.points.map((point, i) => (
                      <li key={i} className="flex gap-2 text-xs leading-relaxed text-zinc-400">
                        <span className="text-cyan-400/70">·</span>
                        <span>{localized(point, lang)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Competitions */}
        <section ref={(el) => { sectionRefs.current.competitions = el; }} className="scroll-mt-24 rounded-3xl border border-white/10 bg-black/35 p-4 shadow-[0_0_35px_rgba(34,211,238,0.06)] backdrop-blur-sm md:p-6">
          <SectionHeading icon={<Trophy size={16} />} title={localized({ zh: '重大赛事', en: 'Major Competitions', ja: '主要大会' }, lang)} subtitle={localized({ zh: '世界级赛事与冠军', en: 'World-class events & champions', ja: '世界大会とチャンピオン' }, lang)} />
          <p className="mt-4 text-sm leading-relaxed text-zinc-400">
            {localized({ zh: '剑玉竞技自 1979 年起便已举办（首场为日本剑玉协会的全日本剑道锦标赛）。目前共有五大赛事，赢得大赛即被视为赢得世界冠军头衔，参赛者通常达 500 人以上。', en: 'Kendama competitions have been held since 1979. There are currently five major competitions; winning one is regarded as a world title, with 500+ participants typically.', ja: 'けん玉競技は1979年から開催されています。現在5つの主要大会があり、優勝は世界タイトルと見なされ、参加者は通常500名以上にのぼります。' }, lang)}
          </p>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {competitions.map((comp) => (
              <div key={localized(comp.name, 'en')} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-pingfang text-sm font-semibold text-white">{localized(comp.name, lang)}</div>
                </div>
                <div className="mt-0.5 text-[11px] text-cyan-300/70">{localized(comp.region, lang)}</div>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400">{localized(comp.desc, lang)}</p>
              </div>
            ))}
          </div>

          {/* Champions tabbed table */}
          <h3 ref={(el) => { sectionRefs.current.champions = el; }} className="mt-6 flex scroll-mt-24 items-center gap-2 font-pingfang text-sm font-semibold text-white">
            <Trophy size={15} className="text-cyan-300" />
            {localized({ zh: '历届冠军一览', en: 'Champions at a glance', ja: '歴代チャンピオン一覧' }, lang)}
          </h3>

          <div className="mt-3 grid grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={() => setChampTab('kwc')}
              className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                champTab === 'kwc'
                  ? 'border-cyan-300/60 bg-cyan-500/20 text-cyan-100'
                  : 'border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10'
              }`}
            >
              <Medal size={14} className={champTab === 'kwc' ? 'text-cyan-300' : 'text-zinc-500'} />
              <span className="whitespace-nowrap font-pingfang">{localized({ zh: '世界杯', en: 'KWC', ja: 'KWC' }, lang)}</span>
            </button>
            <button
              type="button"
              onClick={() => setChampTab('cf')}
              className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                champTab === 'cf'
                  ? 'border-cyan-300/60 bg-cyan-500/20 text-cyan-100'
                  : 'border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10'
              }`}
            >
              <Medal size={14} className={champTab === 'cf' ? 'text-cyan-300' : 'text-zinc-500'} />
              <span className="whitespace-nowrap font-pingfang">{localized({ zh: 'Catch & Flow', en: 'Catch & Flow', ja: 'Catch & Flow' }, lang)}</span>
            </button>
            <button
              type="button"
              onClick={() => setChampTab('leaderboard')}
              className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                champTab === 'leaderboard'
                  ? 'border-cyan-300/60 bg-cyan-500/20 text-cyan-100'
                  : 'border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10'
              }`}
            >
              <Trophy size={14} className={champTab === 'leaderboard' ? 'text-cyan-300' : 'text-zinc-500'} />
              <span className="whitespace-nowrap font-pingfang">{localized({ zh: '总榜', en: 'Leaderboard', ja: '総合' }, lang)}</span>
            </button>
          </div>

          <div className="mt-2 rounded-xl border border-white/10">
            {champTab === 'kwc' && (
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5 text-zinc-400">
                    <th className="px-2 py-2 font-pingfang font-medium">#</th>
                    <th className="px-2 py-2 font-pingfang font-medium">{podiumLabels[lang].winner}</th>
                    <th className="px-2 py-2 font-pingfang font-medium">{podiumLabels[lang].second}</th>
                    <th className="px-2 py-2 font-pingfang font-medium">{podiumLabels[lang].third}</th>
                  </tr>
                </thead>
                <tbody>
                  {kwcWinners.map((row) => (
                    <tr key={row.year} className="border-b border-white/5 text-zinc-300 last:border-0">
                      <td className="px-2 py-2 font-orbitron text-zinc-500">{row.year}</td>
                      <td className="px-2 py-2 font-pingfang"><span className="mr-1.5">{flag(row.winner[0])}</span><span className="break-words">{row.winner[1]}</span></td>
                      <td className="px-2 py-2 font-pingfang"><span className="mr-1.5">{flag(row.second[0])}</span><span className="break-words">{row.second[1]}</span></td>
                      <td className="px-2 py-2 font-pingfang"><span className="mr-1.5">{flag(row.third[0])}</span><span className="break-words">{row.third[1]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {champTab === 'cf' && (
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5 text-zinc-400">
                    <th className="px-2 py-2 font-pingfang font-medium">#</th>
                    <th className="px-2 py-2 font-pingfang font-medium">{podiumLabels[lang].winner}</th>
                    <th className="px-2 py-2 font-pingfang font-medium">{podiumLabels[lang].second}</th>
                    <th className="px-2 py-2 font-pingfang font-medium">{podiumLabels[lang].third}</th>
                  </tr>
                </thead>
                <tbody>
                  {catchAndFlowWinners.map((row) => (
                    <tr key={row.year} className="border-b border-white/5 text-zinc-300 last:border-0">
                      <td className="px-2 py-2 font-orbitron text-zinc-500">{row.year}</td>
                      <td className="px-2 py-2 font-pingfang"><span className="mr-1.5">{flag(row.winner[0])}</span><span className="break-words">{row.winner[1]}</span></td>
                      <td className="px-2 py-2 font-pingfang"><span className="mr-1.5">{flag(row.second[0])}</span><span className="break-words">{row.second[1]}</span></td>
                      <td className="px-2 py-2 font-pingfang"><span className="mr-1.5">{flag(row.third[0])}</span><span className="break-words">{row.third[1]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {champTab === 'leaderboard' && (
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5 text-zinc-400">
                    <th className="px-2 py-2 font-pingfang font-medium">{leaderboardLabels[lang].player}</th>
                    <th className="px-1.5 py-2 text-center font-pingfang font-medium leading-tight">{leaderboardLabels[lang].kwc}</th>
                    <th className="px-1.5 py-2 text-center font-pingfang font-medium leading-tight">{leaderboardLabels[lang].open}</th>
                    <th className="px-1.5 py-2 text-center font-pingfang font-medium leading-tight">{leaderboardLabels[lang].freestyle}</th>
                    <th className="px-1.5 py-2 text-center font-pingfang font-medium leading-tight">{leaderboardLabels[lang].total}</th>
                  </tr>
                </thead>
                <tbody>
                  {championLeaderboard.map((row) => (
                    <tr key={row.name} className="border-b border-white/5 text-zinc-300 last:border-0">
                      <td className="px-2 py-2 font-pingfang"><span className="mr-1.5">{flag(row.code)}</span><span className="break-words">{row.name}</span></td>
                      <td className="px-1.5 py-2 text-center">{row.kwc}</td>
                      <td className="px-1.5 py-2 text-center">{row.open}</td>
                      <td className="px-1.5 py-2 text-center">{row.freestyle}</td>
                      <td className="px-1.5 py-2 text-center font-orbitron font-bold text-cyan-200">{row.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <p className="pt-2 text-center text-[11px] text-zinc-600">{text.source}</p>
      </div>
    </div>
  );
};

const SectionHeading: React.FC<{ icon: React.ReactNode; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <div className="flex items-center gap-2.5">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-cyan-300/30 bg-cyan-500/10 text-cyan-300">
      {icon}
    </span>
    <div className="min-w-0">
      <h2 className="font-pingfang text-base font-bold tracking-wide text-white md:text-lg">{title}</h2>
      <div className="font-orbitron text-[10px] uppercase tracking-[0.2em] text-cyan-100/50 md:text-xs">{subtitle}</div>
    </div>
  </div>
);
